/**
 * Storage Plugin
 *
 * Universal storage abstraction with support for:
 * - Multiple backends (localStorage, sessionStorage, cookie, memory)
 * - TTL/expiration
 * - Namespace isolation
 * - JSON serialization
 * - Automatic fallback
 */

import type { PluginFunction } from '@lytics/sdk-kit';
import { CookieBackend } from './backends/cookie';
import { LocalStorageBackend } from './backends/localStorage';
import { MemoryBackend } from './backends/memory';
import { SessionStorageBackend } from './backends/sessionStorage';
import type { StorageBackend, StorageBackendOptions } from './backends/types';

export type StorageBackendType = 'localStorage' | 'sessionStorage' | 'cookie' | 'memory';

export interface StoragePluginConfig {
  storage?: {
    backend?: StorageBackendType;
    namespace?: string;
    ttl?: number;
    // Cookie-specific defaults
    domain?: string;
    path?: string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
  };
}

export interface StorageOptions extends StorageBackendOptions {
  backend?: StorageBackendType;
  namespace?: string;
}

export interface StoragePlugin {
  set(key: string, value: unknown, options?: StorageOptions): void;
  get<T = unknown>(key: string, options?: Pick<StorageOptions, 'backend'>): T | null;
  remove(key: string, options?: Pick<StorageOptions, 'backend'>): void;
  clear(options?: Pick<StorageOptions, 'backend'>): void;
}

interface StoredValue<T> {
  value: T;
  expires?: number; // Timestamp in milliseconds
}

/**
 * Storage Plugin
 *
 * Provides a universal storage API with automatic backend selection and fallback.
 *
 * @example
 * ```typescript
 * const sdk = new SDK({ storage: { backend: 'localStorage', namespace: 'myapp' } });
 * sdk.use(storagePlugin);
 *
 * sdk.storage.set('user', { id: 123, name: 'Alice' });
 * const user = sdk.storage.get('user');
 * ```
 */
export const storagePlugin: PluginFunction = (plugin, instance, config) => {
  plugin.ns('storage');

  // Set default config
  plugin.defaults({
    storage: {
      backend: 'localStorage',
      namespace: '',
      path: '/',
      sameSite: 'lax',
    },
  });

  // Get config values
  const getBackendType = (): StorageBackendType => config.get('storage.backend') ?? 'localStorage';
  const getNamespace = (): string => config.get('storage.namespace') ?? '';
  const getDefaultTTL = (): number | undefined => config.get('storage.ttl');
  const getCookieDefaults = (): StorageBackendOptions => ({
    domain: config.get('storage.domain'),
    path: config.get('storage.path') ?? '/',
    secure: config.get('storage.secure'),
    sameSite: config.get('storage.sameSite') ?? 'lax',
  });

  // Backend instances (lazy-initialized)
  const backends: Partial<Record<StorageBackendType, StorageBackend>> = {};

  /**
   * Get or create a backend instance
   */
  function getBackend(type: StorageBackendType): StorageBackend {
    if (!backends[type]) {
      switch (type) {
        case 'localStorage':
          backends[type] = new LocalStorageBackend();
          break;
        case 'sessionStorage':
          backends[type] = new SessionStorageBackend();
          break;
        case 'cookie':
          backends[type] = new CookieBackend(getCookieDefaults());
          break;
        case 'memory':
          backends[type] = new MemoryBackend();
          break;
      }
    }
    return backends[type] as StorageBackend;
  }

  /**
   * Build a namespaced key
   */
  function buildKey(key: string, namespace?: string): string {
    const ns = namespace ?? getNamespace();
    return ns ? `${ns}:${key}` : key;
  }

  /**
   * Check if a stored value has expired
   */
  function isExpired(stored: StoredValue<any>): boolean {
    if (!stored.expires) {
      return false;
    }
    return Date.now() > stored.expires;
  }

  /**
   * Set a value in storage
   */
  function set<T>(key: string, value: T, options?: StorageOptions): void {
    const backendType = options?.backend ?? getBackendType();
    const backend = getBackend(backendType);
    const namespacedKey = buildKey(key, options?.namespace);

    // Wrap value with expiration metadata
    const stored: StoredValue<T> = { value };
    const ttl = options?.ttl ?? getDefaultTTL();

    if (ttl) {
      stored.expires = Date.now() + ttl * 1000;
    }

    // Serialize to JSON
    const serialized = JSON.stringify(stored);

    // Store using backend
    backend.set(namespacedKey, serialized, options);

    // Emit event
    plugin.emit('storage:set', { key, value, backend: backendType });
  }

  /**
   * Get a value from storage
   */
  function get<T>(key: string, options?: StorageOptions): T | null {
    const backendType = options?.backend ?? getBackendType();
    const backend = getBackend(backendType);
    const namespacedKey = buildKey(key, options?.namespace);

    // Get from backend
    const serialized = backend.get(namespacedKey);

    if (!serialized) {
      return null;
    }

    try {
      // Deserialize from JSON
      const stored: StoredValue<T> = JSON.parse(serialized);

      // Check expiration
      if (isExpired(stored)) {
        // Remove expired value
        backend.remove(namespacedKey);
        plugin.emit('storage:expired', { key, backend: backendType });
        return null;
      }

      plugin.emit('storage:get', { key, backend: backendType });
      return stored.value;
    } catch (error) {
      console.warn('Failed to parse stored value:', error);
      // Remove corrupted value
      backend.remove(namespacedKey);
      return null;
    }
  }

  /**
   * Remove a value from storage
   */
  function remove(key: string, options?: StorageOptions): void {
    const backendType = options?.backend ?? getBackendType();
    const backend = getBackend(backendType);
    const namespacedKey = buildKey(key, options?.namespace);

    backend.remove(namespacedKey);
    plugin.emit('storage:remove', { key, backend: backendType });
  }

  /**
   * Clear all values from storage
   *
   * If a namespace is provided, only clears values with that namespace prefix.
   * Otherwise, clears all values in the backend.
   */
  function clear(options?: StorageOptions): void {
    const backendType = options?.backend ?? getBackendType();
    const backend = getBackend(backendType);
    const namespace = options?.namespace;

    // If no namespace specified, clear everything
    if (!namespace) {
      backend.clear();
      plugin.emit('storage:clear', { backend: backendType });
      return;
    }

    // Namespace-specific clearing: iterate through all keys and remove matching ones
    if (backendType === 'localStorage' || backendType === 'sessionStorage') {
      const storage = backendType === 'localStorage' ? localStorage : sessionStorage;
      const prefix = `${namespace}:`;
      const keysToRemove: string[] = [];

      // Collect keys to remove
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      // Remove them
      for (const key of keysToRemove) {
        backend.remove(key);
      }
    } else if (backendType === 'cookie') {
      // For cookies, parse document.cookie and remove matching keys
      const prefix = `${namespace}:`;
      const cookies = document.cookie.split(';');

      for (const cookie of cookies) {
        const trimmed = cookie.trim();
        const eqIndex = trimmed.indexOf('=');
        if (eqIndex === -1) continue;

        const encodedKey = trimmed.substring(0, eqIndex);
        const key = decodeURIComponent(encodedKey);

        if (key.startsWith(prefix)) {
          backend.remove(key);
        }
      }
    } else {
      // For memory backend, just clear everything (it's in-memory anyway)
      backend.clear();
    }

    plugin.emit('storage:clear', { backend: backendType, namespace });
  }

  /**
   * Check if a backend is supported
   */
  function isSupported(backendType?: StorageBackendType): boolean {
    const type = backendType ?? getBackendType();
    const backend = getBackend(type);
    return backend.isSupported();
  }

  // Expose public API
  plugin.expose({
    storage: {
      set,
      get,
      remove,
      clear,
      isSupported,
    },
  });

  // Log initialization
  instance.on('sdk:ready', () => {
    const backendType = getBackendType();
    const backend = getBackend(backendType);
    const supported = backend.isSupported();

    if (!supported) {
      console.warn(
        `Storage backend '${backendType}' is not supported, falling back to memory storage`
      );
    }
  });
};

export { CookieBackend } from './backends/cookie';
export { LocalStorageBackend } from './backends/localStorage';
export { MemoryBackend } from './backends/memory';
export { SessionStorageBackend } from './backends/sessionStorage';
// Export types and backends for testing
export type { StorageBackend, StorageBackendOptions } from './backends/types';
